import { useCallback } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import * as Sentry from '@sentry/nextjs'
import { useDispatch, useSelector } from 'react-redux'

import {
  selectIncidentAudioAlert,
  setIncidentAudioAlert,
} from '@/redux/ui/uiSlice'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'

export const useIncidentAudio = () => {
  const dispatch = useDispatch()

  const isFeatureFlagOn = useFeatureFlag(FeatureFlag.incidentAudioAlerts)

  // TODO - In future make this customizable
  const selectedSound = '/audio/incident_notification.mp3'

  const isAudioAlertOn: boolean = useSelector(selectIncidentAudioAlert)

  const toggleAudioEnabled = useCallback(() => {
    const shouldTurnOn = !isAudioAlertOn

    trackEvent(`${BUTTON_PRESS} Settings - Audio Settings - Toggle Alerts`, {
      isNowOn: shouldTurnOn,
    })

    dispatch(setIncidentAudioAlert(shouldTurnOn))
  }, [dispatch, isAudioAlertOn])

  /**
   * If the Setting in on AND the feature flag is on, the sound will attempt
   * to be played.
   * @return Bool if the sound was successfully played
   */
  const playAlertSound = useCallback(async (): Promise<boolean> => {
    // Be sure to also check FF! Failing to do so could result in a state where the FF was on, the setting got
    // turned on, then the FF was removed. Now the user will constantly hear the alerts, without the ability to
    // turn them off
    if (!isAudioAlertOn || !isFeatureFlagOn) {
      return false
    }

    const audioFile = new Audio(selectedSound)
    try {
      await audioFile.play()
      return true
    } catch (e) {
      // Be sure to ship error up, but don't have this break anything
      Sentry.captureException(e)
    }

    return false
  }, [isAudioAlertOn, isFeatureFlagOn, selectedSound])

  return {
    isEnabled: isAudioAlertOn,
    toggleAudioEnabled,
    playAlertSound,
  }
}
